/* iter55i — Admin panel for Microsoft Clarity heat-map configuration.
 *
 * Lets an admin paste the 10-char Clarity project ID and (optionally)
 * a shareable dashboard embed URL from Clarity's Share → Get embed
 * link. When project_id is populated, the tracker script auto-injects
 * on every page and Pro/Premier users see the heat-map iframe in the
 * "Marketing & Business" workspace panel.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { clarityApi } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Save, ExternalLink, TrendingUp, LineChart, RefreshCw } from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';

export default function ClarityAdminPanel() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [projectId, setProjectId]     = useState('');
  const [embedUrl, setEmbedUrl]       = useState('');
  const [enabled, setEnabled]         = useState(true);
  const [hasToken, setHasToken]       = useState(false);
  const [exportToken, setExportToken] = useState('');
  // Feb-2026 — project-live-insights export API state.
  const [insights, setInsights]           = useState(null);
  const [insightsBusy, setInsightsBusy]   = useState(false);
  const [insightsError, setInsightsError] = useState('');
  const [insightsDays, setInsightsDays]   = useState(1);
  const [insightsDim, setInsightsDim]     = useState('');

  useEffect(() => {
    let cancelled = false;
    clarityApi.adminGet().then((r) => {
      if (cancelled) return;
      setProjectId(r?.project_id || '');
      setEmbedUrl(r?.dashboard_embed_url || '');
      setEnabled(r?.enabled !== false);
      setHasToken(Boolean(r?.has_export_token));
    }).catch(() => { /* stay blank on error */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        project_id: projectId.trim(),
        dashboard_embed_url: embedUrl.trim(),
        enabled: enabled,
      };
      // Only send the export token if the user actually typed a new
      // value — otherwise we'd wipe the existing stored token.
      if (exportToken.trim()) body.data_export_api_token = exportToken.trim();
      const r = await clarityApi.adminPut(body);
      setHasToken(Boolean(r?.config?.data_export_api_token));
      setExportToken('');
      toast.success('Clarity configuration saved.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-[12px] text-muted-foreground" data-testid="admin-clarity-loading">
        Loading Clarity settings…
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-5" data-testid="admin-clarity-panel">
      <header className="space-y-1">
        <h2 className="inline-flex items-center gap-2 text-[15px] font-semibold">
          <TrendingUp className="h-4 w-4 text-primary" /> Marketing (Microsoft Clarity)
        </h2>
        <p className="text-[11.5px] text-muted-foreground">
          Configure the Microsoft Clarity project used to collect heat-maps and
          session recordings across every page of The Loom + the Bookstore.
          Once saved, Pro Author and Premier Studio users see the heat-map
          dashboard inside the Marketing &amp; Business workspace panel.
        </p>
      </header>

      <section className="rounded-md border border-border bg-card p-4 space-y-3">
        <div>
          <Label htmlFor="clarity-project-id" className="text-[12px]">Clarity Project ID</Label>
          <Input
            id="clarity-project-id"
            data-testid="admin-clarity-project-id"
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            placeholder="e.g. p6a3xyz9q1"
            maxLength={20}
            className="mt-1 h-8 text-[12px] font-mono"
          />
          <p className="mt-1 text-[10.5px] text-muted-foreground">
            10-character alphanumeric ID from{' '}
            <a href="https://clarity.microsoft.com" target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-0.5 underline hover:no-underline">
              clarity.microsoft.com <ExternalLink className="h-2.5 w-2.5" />
            </a>
            {' '}→ Settings → Setup.
          </p>
        </div>

        <div>
          <Label htmlFor="clarity-embed-url" className="text-[12px]">Dashboard Embed URL (optional)</Label>
          <Input
            id="clarity-embed-url"
            data-testid="admin-clarity-embed-url"
            value={embedUrl}
            onChange={(e) => setEmbedUrl(e.target.value)}
            placeholder="https://clarity.microsoft.com/projects/view/…"
            className="mt-1 h-8 text-[12px]"
          />
          <p className="mt-1 text-[10.5px] text-muted-foreground">
            Paste from Clarity → Share → Get embed link. If left blank, the panel
            shows a &quot;Data is being collected&quot; state with a direct link to Clarity.
          </p>
        </div>

        <div>
          <Label htmlFor="clarity-export-token" className="text-[12px]">
            Data Export API Token (optional)
            {hasToken && (
              <span className="ml-2 rounded bg-emerald-100 px-1.5 py-0.5 text-[9.5px] font-medium text-emerald-800">
                stored
              </span>
            )}
          </Label>
          <Input
            id="clarity-export-token"
            data-testid="admin-clarity-export-token"
            value={exportToken}
            onChange={(e) => setExportToken(e.target.value)}
            placeholder={hasToken ? '••••••••••••  (leave blank to keep current)' : 'clarity_api_…'}
            type="password"
            className="mt-1 h-8 text-[12px] font-mono"
          />
          <p className="mt-1 text-[10.5px] text-muted-foreground">
            Server-side only. Never returned to the client. Used for programmatic
            heat-map exports (roadmap: automated marketing reports).
          </p>
        </div>

        <div className="flex items-center gap-2 pt-1">
          <input
            id="clarity-enabled"
            data-testid="admin-clarity-enabled"
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="h-3.5 w-3.5"
          />
          <Label htmlFor="clarity-enabled" className="cursor-pointer text-[12px]">
            Tracker enabled (uncheck to temporarily stop collecting data)
          </Label>
        </div>
      </section>

      <div className="flex items-center gap-2">
        <Button
          onClick={save}
          disabled={saving}
          data-testid="admin-clarity-save-btn"
          className="h-8 gap-1 text-[12px]"
        >
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
          Save configuration
        </Button>
      </div>

      {/* Feb-2026 — Clarity Data Export API panel. Fetches
          `project-live-insights` server-side using the stored Bearer
          token so admins can chart the last 1-3 days of heat-map
          traffic without opening clarity.microsoft.com. */}
      <section className="rounded-md border border-primary/40 bg-primary/5 p-4 space-y-3"
                data-testid="admin-clarity-live-insights">
        <header className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="inline-flex items-center gap-1.5 text-[13px] font-semibold">
              <LineChart className="h-4 w-4 text-primary" /> Live insights (Data Export API)
            </h3>
            <p className="text-[10.5px] text-muted-foreground">
              Pulls <code className="font-mono text-[10px]">/project-live-insights</code>{' '}
              from Clarity's Data Export API using the stored token.
              Free plan is capped at 10 requests/day.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <label className="flex items-center gap-1 text-[10.5px] uppercase tracking-wider text-muted-foreground">
              Days
              <select
                value={insightsDays}
                onChange={(e) => setInsightsDays(parseInt(e.target.value, 10) || 1)}
                data-testid="admin-clarity-insights-days"
                className="rounded border border-border bg-background px-1.5 py-0.5 text-[11.5px]"
              >
                <option value={1}>1</option>
                <option value={2}>2</option>
                <option value={3}>3</option>
              </select>
            </label>
            <label className="flex items-center gap-1 text-[10.5px] uppercase tracking-wider text-muted-foreground">
              Dim.
              <select
                value={insightsDim}
                onChange={(e) => setInsightsDim(e.target.value)}
                data-testid="admin-clarity-insights-dim"
                className="rounded border border-border bg-background px-1.5 py-0.5 text-[11.5px]"
              >
                <option value="">(none)</option>
                <option value="Browser">Browser</option>
                <option value="Device">Device</option>
                <option value="OS">OS</option>
                <option value="Country">Country</option>
                <option value="Page">Page</option>
                <option value="Source">Source</option>
              </select>
            </label>
            <Button
              size="sm"
              disabled={insightsBusy || !hasToken}
              onClick={async () => {
                setInsightsBusy(true);
                setInsightsError('');
                try {
                  const params = { num_of_days: insightsDays };
                  if (insightsDim) params.dimension1 = insightsDim;
                  const r = await clarityApi.liveInsights(params);
                  setInsights(r);
                } catch (e) {
                  setInsights(null);
                  setInsightsError(e?.response?.data?.detail
                                     || 'Failed to fetch insights.');
                } finally { setInsightsBusy(false); }
              }}
              data-testid="admin-clarity-insights-fetch-btn"
              className="h-8 gap-1 text-[12px]"
            >
              {insightsBusy
                ? <Loader2 className="h-3 w-3 animate-spin" />
                : <RefreshCw className="h-3 w-3" />}
              Fetch insights
            </Button>
          </div>
        </header>
        {!hasToken && (
          <p className="rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-900">
            Save a Data Export API token above before fetching insights.
          </p>
        )}
        {insightsError && (
          <p className="rounded border border-rose-300 bg-rose-50 px-2 py-1.5 text-[11.5px] text-rose-800"
             data-testid="admin-clarity-insights-error">
            {insightsError}
          </p>
        )}
        {insights && (
          <div className="space-y-2" data-testid="admin-clarity-insights-out">
            <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">
              Project {insights.project_id} · last {insights.num_of_days} day{insights.num_of_days === 1 ? '' : 's'}
              {insights.dimensions?.length ? ` · dim: ${insights.dimensions.join(', ')}` : ''}
            </div>
            {/* Feb-2026 — Recharts bar chart per metric. Clarity's payload
                is an array of `{metricName, information: [{name?, value?, ...}]}`
                objects. We flatten the first informationValue on each row
                into a chartable numeric bar. */}
            <ClarityInsightsChart payload={insights.insights} />
            <details className="rounded border border-border bg-background/60">
              <summary className="cursor-pointer px-2 py-1 text-[10.5px] uppercase tracking-wider text-muted-foreground">
                Raw JSON
              </summary>
              <pre className="max-h-72 overflow-auto p-2 font-mono text-[10.5px] leading-snug"
                   data-testid="admin-clarity-insights-json">
{JSON.stringify(insights.insights, null, 2)}
              </pre>
            </details>
          </div>
        )}
      </section>
    </div>
  );
}

/**
 * Feb-2026 — Compact bar chart for `project-live-insights` payload.
 *
 * Clarity's response shape (as of Feb 2026) is:
 *   [ { metricName: "Scroll Depth", information: [ { name?, value } ] } ]
 * where `value` is a numeric string. We plot metric name (X) vs.
 * numeric value (Y) as a single bar chart per metric row. When the
 * payload is empty (Clarity returns `[]` for a fresh project) we show
 * a small "no data yet" placeholder so admins know nothing is broken.
 */
function ClarityInsightsChart({ payload }) {
  const data = useMemo(() => {
    if (!Array.isArray(payload)) return [];
    return payload.map((row, idx) => {
      const info = Array.isArray(row?.information) ? row.information[0] : null;
      const rawValue = info?.value ?? info?.metricValue ?? row?.metricValue;
      const numeric = Number(rawValue);
      const label = row?.metricName || info?.name || `Metric ${idx + 1}`;
      return {
        name: label.length > 22 ? `${label.slice(0, 20)}…` : label,
        fullName: label,
        value: Number.isFinite(numeric) ? numeric : 0,
      };
    }).filter((r) => r.value !== 0);
  }, [payload]);
  if (!data.length) {
    return (
      <div className="rounded border border-dashed border-border bg-background/40 px-3 py-4 text-center text-[11px] text-muted-foreground"
           data-testid="admin-clarity-insights-empty">
        No chartable metrics returned yet — Clarity typically populates the
        first row after ~500 pageviews per project.
      </div>
    );
  }
  return (
    <div className="h-56 w-full" data-testid="admin-clarity-insights-chart">
      <ResponsiveContainer>
        <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
          <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-12} height={40} />
          <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
          <Tooltip
            formatter={(v) => v.toLocaleString()}
            labelFormatter={(_, items) => items[0]?.payload?.fullName || ''}
            contentStyle={{ fontSize: 11 }}
          />
          <Bar dataKey="value" fill="#3b82f6" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
